import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import api from '../services/api';
import { Feedback, Project } from '../types';

interface FeedbackWithProject extends Feedback {
  project?: Project;
}

const FeedbackPage: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<FeedbackWithProject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackWithProject | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);

  const fetchFeedbacks = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/feedback');
      if (response.success) {
        const feedbackData = response.data || [];

        // Fetch project details for each feedback
        const feedbacksWithProjects = await Promise.all(
          feedbackData.map(async (feedback: FeedbackWithProject) => {
            if (feedback.projectId) {
              try {
                const projectResponse = await api.getProjectById(feedback.projectId);
                if (projectResponse.success) {
                  return { ...feedback, project: projectResponse.data };
                }
              } catch (err) {
                console.warn(`Failed to fetch project for feedback ${feedback.id}:`, err);
              }
            }
            return feedback;
          })
        );

        setFeedbacks(feedbacksWithProjects);
        setError('');
      } else {
        setError(response.message || 'Failed to fetch feedback');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch feedback');
      setFeedbacks([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  const handleOpenDialog = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setResponseText(feedback.response || '');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFeedback(null);
    setResponseText('');
  };

  const handleSubmitResponse = async () => {
    if (!selectedFeedback || !responseText.trim()) {
      setError('Response cannot be empty');
      return;
    }

    setIsSubmittingResponse(true);
    try {
      const res = await api.put(`/feedback/${selectedFeedback.id}/response`, {
        response: responseText.trim(),
      });

      if (res.success) {
        const updated = res.data;
        const updatedFeedbacks = feedbacks.map(f =>
          f.id === updated.id ? { ...f, ...updated } : f
        );
        setFeedbacks(updatedFeedbacks);
        setError('');
      } else {
        setError(res.message || 'Failed to submit response');
      }
      handleCloseDialog();
    } catch (err) {
      setError('Failed to submit response');
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'responded':
        return 'success';
      case 'new':
        return 'warning';
      case 'closed':
        return 'error';
      default:
        return 'info';
    }
  };

  const getFeedbackTypeColor = (type: string) => {
    switch (type) {
      case 'suggestion':
        return 'info';
      case 'complaint':
        return 'error';
      case 'appreciation':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {'Manage Feedback'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>{'Name'}</TableCell>
                  <TableCell>{'Project'}</TableCell>
                  <TableCell>{'Type'}</TableCell>
                  <TableCell>{'Message'}</TableCell>
                  <TableCell>{'Image'}</TableCell>
                  <TableCell>{'Status'}</TableCell>
                  <TableCell>{'Date'}</TableCell>
                  <TableCell align="right">{'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feedbacks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography color="textSecondary">
                        {'No feedback available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  feedbacks.map(feedback => (
                    <TableRow key={feedback.id} hover>
                      <TableCell>
                        {feedback.name || 'Anonymous'}
                      </TableCell>
                      <TableCell>
                        {feedback.project ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {feedback.project.title}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Project not found
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={feedback.feedbackType}
                          color={getFeedbackTypeColor(feedback.feedbackType) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" noWrap>
                          {feedback.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {feedback.imageUrl && (
                          <Button
                            size="small"
                            onClick={() => window.open(feedback.imageUrl!, '_blank')}
                          >
                            View Image
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={feedback.status}
                          color={getStatusColor(feedback.status)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(feedback.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenDialog(feedback)}
                        >
                          {feedback.status === 'acknowledged' ? 'View' : 'Respond'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Response Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {'Respond to Feedback'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedFeedback && (
            <Box sx={{ mb: 3 }}>
              {selectedFeedback.project && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {'Project Information'}:
                  </Typography>
                  <Typography variant="body2">
                    <strong>{selectedFeedback.project.title}</strong>
                  </Typography>
                </Box>
              )}
              <Typography variant="subtitle2" color="textSecondary">
                {'Original Feedback'}:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                {selectedFeedback.message}
              </Typography>
              {selectedFeedback.imageUrl && (
                <Button
                  size="small"
                  onClick={() => window.open(selectedFeedback.imageUrl!, '_blank')}
                  sx={{ mb: 1 }}
                >
                  View Image
                </Button>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            label={'Your Response'}
            multiline
            rows={4}
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            disabled={isSubmittingResponse}
            placeholder={'Enter your response here...'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSubmittingResponse}>
            {'Cancel'}
          </Button>
          <Button
            onClick={handleSubmitResponse}
            variant="contained"
            disabled={isSubmittingResponse}
          >
            {isSubmittingResponse ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FeedbackPage;
